import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Search, Filter, MapPin, Package, X, ChevronDown, AlertCircle, ShoppingCart, Lock, ImageOff, Star, ShieldCheck } from 'lucide-react';
import AppHeader from '@/components/AppHeader';
import AppFooter from '@/components/AppFooter';
import StatusBadge from '@/components/ui/StatusBadge';
import OrderModal from '@/components/OrderModal';
import { CATEGORIES } from '@/lib/constants';
import { formatINR } from '@/lib/format';
import { listingsApi } from '@/lib/api';
import { getStoredUser, clearAuth } from '@/lib/authStore';
import { Toaster, toast } from 'sonner';

// ─── Listing Card ─────────────────────────────────────────
function ListingCard({ listing, onOrderClick, canOrder, isGuest }) {
  const navigate = useNavigate();

  return (
    <div
      className="listing-card flex flex-col h-full cursor-pointer"
      onClick={() => navigate(`/marketplace/${listing.id}`)}
    >
      {/* Product Image */}
      <div className="relative w-full overflow-hidden" style={{ height: '160px', backgroundColor: 'var(--background)' }}>
        {/* Featured ribbon — premium/upgraded seller placement */}
        {listing.isFeatured && (
          <div
            className="absolute top-0 left-0 z-10 flex items-center gap-1 px-2 py-1 text-xs font-heading tracking-stamp"
            style={{ backgroundColor: 'var(--amber)', color: 'white', fontFamily: 'var(--font-heading)' }}
          >
            <Star size={10} fill="white" />
            FEATURED
          </div>
        )}
        {listing.imageUrl ? (
          <img
            src={listing.imageUrl}
            alt={listing.title}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.style.display = 'none';
              const parent = e.target.parentElement;
              if (parent) {
                const fallback = parent.querySelector('.img-fallback');
                if (fallback) fallback.style.display = 'flex';
              }
            }}
          />
        ) : null}
        <div
          className="img-fallback absolute inset-0 flex flex-col items-center justify-center gap-1"
          style={{ display: listing.imageUrl ? 'none' : 'flex', backgroundColor: 'var(--background)' }}
        >
          <ImageOff size={24} style={{ color: 'var(--muted-foreground)' }} />
          <span className="text-xs font-heading tracking-stamp" style={{ color: 'var(--muted-foreground)', fontFamily: 'var(--font-heading)' }}>
            NO IMAGE
          </span>
        </div>
        {/* Guest overlay hint */}
        {isGuest && (
          <div
            className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity"
            style={{ backgroundColor: 'rgba(0,0,0,0.55)' }}
          >
            <div className="flex flex-col items-center gap-1">
              <Lock size={20} style={{ color: 'white' }} />
              <span className="text-xs font-heading tracking-stamp text-white" style={{ fontFamily: 'var(--font-heading)' }}>
                LOGIN TO ORDER
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Category Bar */}
      <div
        className="px-3 py-2 border-b border-border flex items-center justify-between"
        style={{ backgroundColor: 'var(--background)' }}
      >
        <span
          className="text-xs font-heading tracking-stamp"
          style={{ color: 'var(--muted-foreground)', fontFamily: 'var(--font-heading)' }}
        >
          {listing.category.toUpperCase()}
        </span>
        <StatusBadge status={listing.status} size="sm" />
      </div>

      {/* Content */}
      <div className="p-4 flex flex-col flex-1">
        <h3 className="text-sm font-medium leading-snug mb-3 line-clamp-2" style={{ minHeight: '2.5rem' }}>
          {listing.title}
        </h3>

        {/* Metrics Row */}
        <div className="grid grid-cols-2 gap-2 mb-3">
          {/* Price — blurred for guests */}
          <div className="p-2 border relative" style={{ borderColor: 'var(--border)', backgroundColor: 'var(--background)' }}>
            <div className="flex items-center justify-between mb-0.5">
              <p className="text-xs font-heading tracking-stamp" style={{ color: 'var(--muted-foreground)', fontFamily: 'var(--font-heading)' }}>
                UNIT PRICE
              </p>
              {listing.discountPercent > 0 && (
                <span
                  className="text-xs font-heading tracking-stamp px-1 py-0.5"
                  style={{ backgroundColor: 'var(--secondary)', color: 'white', fontFamily: 'var(--font-heading)', fontSize: '10px' }}
                >
                  {listing.discountPercent}% OFF
                </span>
              )}
            </div>
            {isGuest ? (
              <div className="flex items-center gap-1">
                <p
                  className="text-base font-heading select-none"
                  style={{
                    color: 'var(--primary)',
                    fontFamily: 'var(--font-mono)',
                    filter: 'blur(5px)',
                    userSelect: 'none',
                    pointerEvents: 'none',
                  }}
                >
                  {formatINR(listing.price)}
                </p>
                <Lock size={11} style={{ color: 'var(--amber)', flexShrink: 0 }} />
              </div>
            ) : (
              <div className="flex items-baseline gap-1.5">
                <p
                  className="text-base font-heading"
                  style={{ color: 'var(--primary)', fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums' }}
                >
                  {formatINR(listing.price)}
                </p>
                {listing.marketValue > listing.price && (
                  <p className="text-xs line-through" style={{ color: 'var(--muted-foreground)', fontFamily: 'var(--font-mono)' }}>
                    {formatINR(listing.marketValue)}
                  </p>
                )}
              </div>
            )}
          </div>
          {/* Quantity — always visible */}
          <div className="p-2 border" style={{ borderColor: 'var(--border)', backgroundColor: 'var(--background)' }}>
            <p className="text-xs font-heading tracking-stamp mb-0.5" style={{ color: 'var(--muted-foreground)', fontFamily: 'var(--font-heading)' }}>
              QTY AVAIL.
            </p>
            <p
              className="text-base font-heading"
              style={{ color: 'var(--foreground)', fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums' }}
            >
              {listing.quantity.toLocaleString('en-IN')}
            </p>
          </div>
        </div>

        {/* Description */}
        <p className="text-xs leading-relaxed mb-3 flex-1 line-clamp-2" style={{ color: 'var(--muted-foreground)' }}>
          {listing.description}
        </p>

        {/* Seller Info */}
        <div className="pt-3 border-t border-border">
          <div className="flex items-center gap-1 mb-1">
            <Package size={11} style={{ color: 'var(--muted-foreground)' }} />
            <p className="text-xs font-medium truncate">{listing.sellerCompany}</p>
            {listing.sellerVerified && (
              <ShieldCheck size={12} style={{ color: 'var(--secondary)', flexShrink: 0 }} titleAccess="Verified Seller" />
            )}
          </div>
          <div className="flex items-center gap-1">
            <MapPin size={11} style={{ color: 'var(--muted-foreground)' }} />
            <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{listing.sellerCity}</p>
          </div>
        </div>

        {/* CTA */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onOrderClick(listing);
          }}
          disabled={!canOrder && !isGuest}
          className={`mt-3 w-full py-2.5 text-xs flex items-center justify-center gap-1.5 transition-all duration-150 ${
            canOrder
              ? 'btn-primary'
              : isGuest
              ? 'btn-secondary font-heading tracking-stamp' : 'border-2 border-border text-muted-foreground cursor-not-allowed font-heading tracking-stamp'
          }`}
          style={{ fontFamily: 'var(--font-heading)' }}
          title={isGuest ? 'Log in to view price and place orders' : !canOrder ? 'Seller accounts cannot place orders' : 'Place an order for this listing'}
        >
          {isGuest ? <Lock size={13} /> : <ShoppingCart size={13} />}
          {canOrder ? 'PLACE ORDER' : isGuest ? 'LOGIN TO ORDER' : 'BUYER LOGIN REQUIRED'}
        </button>
      </div>
    </div>
  );
}

// ─── Main Marketplace Client ──────────────────────────────
export default function MarketplaceClient() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);

  const [listings, setListings] = useState([]);
  const [listingsLoading, setListingsLoading] = useState(true);
  const [listingsError, setListingsError] = useState('');

  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '');
  const [selectedCity, setSelectedCity] = useState('');
  const [orderTarget, setOrderTarget] = useState(null);

  // Auth check — no redirect, guests can browse
  useEffect(() => {
    const storedUser = getStoredUser();
    setUser(storedUser);
    setAuthChecked(true);
  }, []);

  // Fetch listings whenever filters change (debounced for free-text search).
  useEffect(() => {
    let cancelled = false;
    setListingsLoading(true);
    setListingsError('');

    const timeout = setTimeout(() => {
      listingsApi
        .list({ search, category: selectedCategory, city: selectedCity })
        .then((data) => {
          if (!cancelled) setListings(data.listings);
        })
        .catch((err) => {
          if (!cancelled) setListingsError(err.message);
        })
        .finally(() => {
          if (!cancelled) setListingsLoading(false);
        });
    }, search ? 300 : 0);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [search, selectedCategory, selectedCity]);

  const cities = useMemo(() => {
    const unique = Array.from(new Set(listings.map((l) => l.sellerCity)));
    return unique.sort();
  }, [listings]);

  // Filtering (category/city/search) is now done server-side by the API call
  // above; this list is exactly what the backend returned.
  const filteredListings = listings;

  const handleOrderSuccess = (qty) => {
    if (!orderTarget) return;
    toast.success(`Payment confirmed! ${qty} × ${orderTarget.title.slice(0, 30)}... sent to the seller.`);
    // Reflect the reserved stock locally (the backend already decremented it at checkout).
    setListings((prev) =>
      prev.map((l) => (l.id === orderTarget.id ? { ...l, quantity: l.quantity - qty } : l))
    );
    setOrderTarget(null);
  };

  const handleOrderClick = (listing) => {
    if (!user) {
      // Guest: redirect to login
      navigate('/login');
      return;
    }
    if (user.role === 'buyer') {
      setOrderTarget(listing);
    }
  };

  const handleLogout = () => {
    clearAuth();
    navigate('/');
  };

  const clearFilters = () => {
    setSearch('');
    setSelectedCategory('');
    setSelectedCity('');
  };

  const hasFilters = search || selectedCategory || selectedCity;
  const canOrder = user?.role === 'buyer';

  if (!authChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--background)' }}>
        <div className="text-center">
          <div className="inline-block w-8 h-8 border-2 border-t-transparent rounded-full animate-spin mb-3" style={{ borderColor: 'var(--primary)' }} />
          <p className="text-sm font-heading tracking-stamp" style={{ fontFamily: 'var(--font-heading)' }}>LOADING MARKETPLACE...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: 'var(--background)' }}>
      <AppHeader
        userName={user?.name}
        userRole={user?.role}
        onLogout={handleLogout}
        currentPath="/marketplace"
      />

      <main className="flex-1 max-w-screen-2xl mx-auto w-full px-4 lg:px-8 xl:px-10 2xl:px-16 py-6">
        {/* Page Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-1 h-6" style={{ backgroundColor: 'var(--primary)' }} />
              <h1
                className="font-heading text-2xl tracking-stamp"
                style={{ fontFamily: 'var(--font-heading)' }}
              >
                MARKETPLACE
              </h1>
            </div>
            <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
              Browse active deadstock listings from verified Indian sellers
            </p>
          </div>

          {!user && (
            <div
              className="flex items-center gap-2 px-3 py-2 border text-sm"
              style={{
                backgroundColor: 'var(--amber-bg)',
                borderColor: 'var(--amber)',
                color: 'var(--amber)',
              }}
            >
              <Lock size={14} />
              <span className="text-xs font-heading tracking-stamp" style={{ fontFamily: 'var(--font-heading)' }}>
                LOG IN TO VIEW PRICES & PLACE ORDERS
              </span>
            </div>
          )}

          {user?.role === 'seller' && (
            <div
              className="flex items-center gap-2 px-3 py-2 border text-sm"
              style={{
                backgroundColor: 'var(--amber-bg)',
                borderColor: 'var(--amber)',
                color: 'var(--amber)',
              }}
            >
              <AlertCircle size={14} />
              <span className="text-xs font-heading tracking-stamp" style={{ fontFamily: 'var(--font-heading)' }}>
                SELLER ACCOUNT — BROWSE ONLY
              </span>
            </div>
          )}
        </div>

        {/* Filter Bar */}
        <div
          className="p-4 border-2 border-foreground ink-shadow-sm mb-6"
          style={{ backgroundColor: 'var(--card)' }}
        >
          <div className="flex flex-wrap gap-3 items-end">
            {/* Search */}
            <div className="flex-1 min-w-[200px]">
              <label className="block text-xs font-heading tracking-stamp mb-1.5" style={{ fontFamily: 'var(--font-heading)' }}>
                SEARCH LISTINGS
              </label>
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by title, description, seller..."
                  className="input-ledger w-full pl-9 pr-3 py-2.5 text-sm"
                />
              </div>
            </div>

            {/* Category */}
            <div className="min-w-[160px]">
              <label className="block text-xs font-heading tracking-stamp mb-1.5" style={{ fontFamily: 'var(--font-heading)' }}>
                CATEGORY
              </label>
              <div className="relative">
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="input-ledger w-full px-3 py-2.5 text-sm appearance-none pr-8"
                >
                  <option value="">All Categories</option>
                  {CATEGORIES.map((cat) => (
                    <option key={`cat-opt-${cat}`} value={cat}>{cat}</option>
                  ))}
                </select>
                <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-muted-foreground" />
              </div>
            </div>

            {/* City */}
            <div className="min-w-[140px]">
              <label className="block text-xs font-heading tracking-stamp mb-1.5" style={{ fontFamily: 'var(--font-heading)' }}>
                CITY
              </label>
              <div className="relative">
                <select
                  value={selectedCity}
                  onChange={(e) => setSelectedCity(e.target.value)}
                  className="input-ledger w-full px-3 py-2.5 text-sm appearance-none pr-8"
                >
                  <option value="">All Cities</option>
                  {cities.map((city) => (
                    <option key={`city-opt-${city}`} value={city}>{city}</option>
                  ))}
                </select>
                <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-muted-foreground" />
              </div>
            </div>

            {/* Clear Filters */}
            {hasFilters && (
              <button
                onClick={clearFilters}
                className="btn-secondary px-3 py-2.5 text-xs flex items-center gap-1.5 self-end"
              >
                <X size={13} />
                CLEAR
              </button>
            )}
          </div>

          {/* Active filter chips */}
          {hasFilters && (
            <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-border">
              <span className="text-xs font-heading tracking-stamp" style={{ color: 'var(--muted-foreground)', fontFamily: 'var(--font-heading)' }}>
                FILTERS:
              </span>
              {search && (
                <span
                  className="inline-flex items-center gap-1 px-2 py-0.5 text-xs border"
                  style={{ borderColor: 'var(--primary)', color: 'var(--primary)', backgroundColor: 'rgba(193,64,29,0.05)' }}
                >
                  <Search size={10} />
                  {search}
                  <button onClick={() => setSearch('')}><X size={10} /></button>
                </span>
              )}
              {selectedCategory && (
                <span
                  className="inline-flex items-center gap-1 px-2 py-0.5 text-xs border"
                  style={{ borderColor: 'var(--primary)', color: 'var(--primary)', backgroundColor: 'rgba(193,64,29,0.05)' }}
                >
                  <Filter size={10} />
                  {selectedCategory}
                  <button onClick={() => setSelectedCategory('')}><X size={10} /></button>
                </span>
              )}
              {selectedCity && (
                <span
                  className="inline-flex items-center gap-1 px-2 py-0.5 text-xs border"
                  style={{ borderColor: 'var(--primary)', color: 'var(--primary)', backgroundColor: 'rgba(193,64,29,0.05)' }}
                >
                  <MapPin size={10} />
                  {selectedCity}
                  <button onClick={() => setSelectedCity('')}><X size={10} /></button>
                </span>
              )}
            </div>
          )}
        </div>

        {/* Results Header */}
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
            <span className="font-mono-data font-medium" style={{ color: 'var(--foreground)', fontFamily: 'var(--font-mono)' }}>
              {filteredListings.length}
            </span>{' '}
            {filteredListings.length === 1 ? 'listing' : 'listings'} found
            {hasFilters && ' (filtered)'}
          </p>
          {!listingsLoading && (
            <p className="text-xs font-mono-data" style={{ color: 'var(--muted-foreground)', fontFamily: 'var(--font-mono)' }}>
              Updated {new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
            </p>
          )}
        </div>

        {/* Listings Grid */}
        {listingsLoading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div
              className="inline-block w-8 h-8 border-2 border-t-transparent rounded-full animate-spin mb-3"
              style={{ borderColor: 'var(--primary)' }}
            />
            <p className="text-sm font-heading tracking-stamp" style={{ fontFamily: 'var(--font-heading)' }}>
              LOADING LISTINGS...
            </p>
          </div>
        ) : listingsError ? (
          <div
            className="flex flex-col items-center justify-center py-20 border-2 border-dashed"
            style={{ borderColor: 'var(--danger)' }}
          >
            <AlertCircle size={36} className="mb-3" style={{ color: 'var(--danger)' }} />
            <h3 className="font-heading text-base tracking-stamp mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
              COULDN'T LOAD LISTINGS
            </h3>
            <p className="text-sm text-center max-w-sm" style={{ color: 'var(--muted-foreground)' }}>
              {listingsError}
            </p>
          </div>
        ) : filteredListings.length === 0 ? (
          <div
            className="flex flex-col items-center justify-center py-20 border-2 border-dashed"
            style={{ borderColor: 'var(--border)' }}
          >
            <Package size={40} className="mb-4" style={{ color: 'var(--muted-foreground)' }} />
            <h3 className="font-heading text-lg tracking-stamp mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
              NO LISTINGS FOUND
            </h3>
            <p className="text-sm text-center max-w-sm mb-4" style={{ color: 'var(--muted-foreground)' }}>
              No active deadstock listings match your current filters. Try adjusting your search criteria or clearing filters.
            </p>
            <button onClick={clearFilters} className="btn-primary px-4 py-2 text-sm">
              CLEAR ALL FILTERS
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-4 gap-4">
            {filteredListings.map((listing) => (
              <ListingCard
                key={listing.id}
                listing={listing}
                onOrderClick={handleOrderClick}
                canOrder={canOrder}
                isGuest={!user}
              />
            ))}
          </div>
        )}
      </main>

      <AppFooter />

      {/* Order Modal */}
      {orderTarget && (
        <OrderModal
          listing={orderTarget}
          onClose={() => setOrderTarget(null)}
          onSuccess={handleOrderSuccess}
        />
      )}

      <Toaster position="bottom-right" richColors />
    </div>
  );
}
